import { useEffect, useState } from "react";

import { Alert } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { getTrackingSteps } from "@/services/clientAreaService";
import { FaCircleInfo } from "react-icons/fa6";
import TransportTimelineV2, {
  DriverInfoV2,
  StepV2,
} from "./transport-timeline-v2";

interface TransportTimelineV2WrapperProps {
  cve_codigo: string;
  cve_chassi: string;
  driver?: DriverInfoV2;
}

export default function TransportTimelineV2Wrapper({
  cve_codigo,
  cve_chassi,
  driver,
}: TransportTimelineV2WrapperProps) {
  const [steps, setSteps] = useState<StepV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const fetchTracking = async () => {
      try {
        setLoading(true);
        const rawSteps = await getTrackingSteps(cve_codigo, cve_chassi);

        if (!rawSteps || rawSteps.length === 0) {
          setHasError(true);
          return;
        }

        const mappedSteps: StepV2[] = rawSteps.map((text, index) => {
          const formatDescription = (description: string) => {
            let formatted = description;

            const dateRegex = /(\d{2}\/\d{2}\/\d{4})/g;
            formatted = formatted.replace(
              dateRegex,
              '<strong class="text-n-green font-semibold">$1</strong>',
            );

            const locationRegex = /([A-ZÁÊÇÕ\s]+)\s-\s([A-Z]{2})/g;
            formatted = formatted.replace(
              locationRegex,
              '<strong class="text-gray-900 font-medium">$1 - $2</strong>',
            );

            const actionWords = ["recebido", "embarcou", "chegou", "retirado"];
            actionWords.forEach((word) => {
              const actionRegex = new RegExp(`\\b(${word})\\b`, "gi");
              formatted = formatted.replace(
                actionRegex,
                '<span class="font-medium text-n-green">$1</span>',
              );
            });

            formatted = formatted.replace(/\.\s+([A-ZÁÉÍÓÚÀÂÊÎÔÛÃÕÇÜ])/g, ".<br/>$1");

            const previsaoRegex = /(Previsão[^.]*)/g;
            formatted = formatted.replace(
              previsaoRegex,
              '<span class="text-n-green-6 font-medium italic">$1</span>',
            );

            return formatted;
          };

          return {
            label: `Etapa ${index + 1}`,
            description: text,
            formattedDescription: formatDescription(text),
            status: "completed",
          };
        });

        if (mappedSteps.length > 0) {
          mappedSteps[mappedSteps.length - 1].status = "current";
        }

        setSteps(mappedSteps);
      } catch {
        setHasError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchTracking();
  }, [cve_codigo, cve_chassi]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold text-n-green">Rastreamento:</h3>
          <div className="flex items-start">
            <FaCircleInfo className="inline mr-2 text-gray-400" size={16} />
            <p className="text-sm text-n-text-neutral">
              Prezado cliente, você está vendo as mesmas informações de status
              do seu transporte que nossos agentes de atendimento ao cliente
              podem acessar.
            </p>
          </div>
        </div>
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="flex gap-4">
            <Skeleton className="w-10 h-10 rounded-full" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (hasError || steps.length === 0) {
    return (
      <Alert variant="default">
        <p className="text-gray-700 font-medium">
          Rastreamento indisponível no momento.
        </p>
        <p className="text-sm text-muted-foreground">
          O rastreamento ainda não está disponível para este veículo ou ocorreu
          um erro ao buscar os dados.
        </p>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="space-y-1">
        <h3 className="text-lg font-semibold text-n-green">Rastreamento:</h3>
        <div className="flex items-start ">
          <FaCircleInfo className="inline mr-2 text-gray-400 mt-1" size={16} />
          <p className="text-sm text-n-text">
            Prezado cliente, você está vendo as mesmas informações de status do
            seu transporte que nossos agentes de atendimento ao cliente podem
            acessar.
          </p>
        </div>
      </div>
      <TransportTimelineV2 steps={steps} driver={driver} />
    </div>
  );
}
